/**
 * As consultas da fila de triagem.
 *
 * Ao contrário de `/casos`, `GET /triagem` devolve um ENVELOPE com `total`
 * (`TriagemFilaResponse`, `T7`) — a paginação aqui é real, não inferida por
 * "página veio cheia".
 *
 * `opcoesDaTriagemDetalhada` trata `404` como sucesso com `null` (mesmo
 * contrato que `opcoesDoCaso` já cumpre para `DetalheDoCaso`) — sem isso,
 * `requisitar()` lançaria em qualquer `!ok`, inclusive `404`, e a tela cairia
 * em `EstadoDeErro` em vez de `EstadoVazio` (achado do `architect-review`,
 * bloqueava o aceite de `T13`).
 */

import { queryOptions } from '@tanstack/react-query';
import type { MessageKindFilter, TriagemDetalhe, TriagemFilaResponse } from '@anery/api-contracts';

import { montarUrlDaApi, requisitar, requisitarTexto } from '../../../shared/api/clienteHttp';
import { ErroDaApi } from '../../../shared/api/erros';
import { mapearFilaDeTriagem, mapearTriagemDetalhada } from '../../../shared/triagem/mapeadores';
import type { FilaDeTriagem, TriagemDetalhada } from '../../../shared/triagem/tipos';

/** Cabe no teto de `ITriagemQueries.TamanhoMaximoDePagina` (100, `T7`). */
export const TAMANHO_DE_PAGINA = 50;

/** O que a tela escolhe. `tipoMensagem: null` = sem filtro. */
export interface FiltrosDaFila {
  readonly pagina: number;
  readonly somenteAConfirmar: boolean;
  readonly tipoMensagem: MessageKindFilter | null;
  readonly maisRecentesPrimeiro: boolean;
  /** `TP-038` (`D6`): só os tickets cuja classificação de tipo e de operadora pede conferência. */
  readonly somenteAConferirTipoEOperadora: boolean;
  /** Id do cadastro (`GET /operadoras`); `null` = todas as operadoras. */
  readonly operadoraId: string | null;
  /** A modalidade do e-mail de origem; `null` = todas. */
  readonly modalidade: FiltroDeModalidade | null;
}

/** `sem` = o ticket sem modalidade (o tipo não indica e o agente não leu). `VM` é ventilação mecânica, tipo de `ID`. */
export const FILTROS_DE_MODALIDADE = ['AD', 'ID', 'VM', 'sem'] as const;
export type FiltroDeModalidade = (typeof FILTROS_DE_MODALIDADE)[number];

interface ParametrosDaFila {
  readonly page: number;
  readonly pageSize: number;
  readonly somenteAConfirmar: boolean;
  readonly tipoMensagem: MessageKindFilter | null;
  readonly maisRecentesPrimeiro: boolean;
  readonly somenteAConferirTipoEOperadora: boolean;
  readonly operadoraId: string | null;
  readonly modalidade: FiltroDeModalidade | null;
}

export const chavesDeTriagem = {
  raiz: ['fila-de-triagem'] as const,
  lista: (parametros: ParametrosDaFila) => ['fila-de-triagem', 'lista', parametros] as const,
  detalhe: (ticketId: string) => ['fila-de-triagem', 'detalhe', ticketId] as const,
};

function montarConsulta(parametros: ParametrosDaFila): string {
  const busca = new URLSearchParams({
    page: String(parametros.page),
    pageSize: String(parametros.pageSize),
    somenteAConfirmar: String(parametros.somenteAConfirmar),
  });
  if (parametros.tipoMensagem !== null) busca.set('tipoMensagem', parametros.tipoMensagem);
  if (parametros.maisRecentesPrimeiro) busca.set('maisRecentesPrimeiro', 'true');
  if (parametros.somenteAConferirTipoEOperadora) busca.set('classificacao', 'a_revisar');
  if (parametros.operadoraId !== null) busca.set('operadora', parametros.operadoraId);
  if (parametros.modalidade !== null) busca.set('modalidade', parametros.modalidade);
  return `?${busca.toString()}`;
}

function buscarFila(parametros: ParametrosDaFila, sinal: AbortSignal): Promise<FilaDeTriagem> {
  return requisitar<TriagemFilaResponse>(`/triagem${montarConsulta(parametros)}`, { sinal }).then(
    mapearFilaDeTriagem,
  );
}

/**
 * Uma página da fila. `pagina` é 1-based, como a API.
 *
 * `somenteAConfirmar`, `tipoMensagem`, `operadoraId` e `modalidade` são filtros DA API (`AC-10`, `TP-028`)
 * — a fila é paginada de verdade, e filtrar só no client esconderia o que
 * está nas outras páginas.
 */
export function opcoesDaFilaDeTriagem(filtros: FiltrosDaFila) {
  const parametros: ParametrosDaFila = {
    page: filtros.pagina,
    pageSize: TAMANHO_DE_PAGINA,
    somenteAConfirmar: filtros.somenteAConfirmar,
    tipoMensagem: filtros.tipoMensagem,
    maisRecentesPrimeiro: filtros.maisRecentesPrimeiro,
    somenteAConferirTipoEOperadora: filtros.somenteAConferirTipoEOperadora,
    operadoraId: filtros.operadoraId,
    modalidade: filtros.modalidade,
  };
  return queryOptions({
    queryKey: chavesDeTriagem.lista(parametros),
    queryFn: ({ signal }) => buscarFila(parametros, signal),
  });
}

async function buscarDetalhe(ticketId: string, sinal: AbortSignal): Promise<TriagemDetalhada | null> {
  try {
    const detalhe = await requisitar<TriagemDetalhe>(`/triagem/${encodeURIComponent(ticketId)}`, { sinal });
    return mapearTriagemDetalhada(detalhe);
  } catch (causa) {
    // Ticket inexistente, de outro núcleo, ou que não é de triagem: a API
    // devolve a MESMA resposta para os três (`T5`/`T7`) — 404, sem
    // distinção. Aqui vira sucesso com `null`, para a tela decidir
    // `EstadoVazio`, nunca `EstadoDeErro`.
    if (causa instanceof ErroDaApi && causa.status === 404) return null;
    throw causa;
  }
}

/** Um ticket da triagem, pelo id. `null` (sucesso) quando a API respondeu `404`. */
export function opcoesDaTriagemDetalhada(ticketId: string) {
  return queryOptions({
    queryKey: chavesDeTriagem.detalhe(ticketId),
    queryFn: ({ signal }) => buscarDetalhe(ticketId, signal),
  });
}

/**
 * Um `.eml` já arquivado não muda, e o de e-mail real tem centenas de KB: o padrão de 30 s refazia o
 * download a cada foco da janela. Fica em cache enquanto a tela o usa e é descartado logo depois, para
 * não segurar corpo de e-mail de paciente na memória do navegador.
 */
const CACHE_DO_EML = { staleTime: Infinity, gcTime: 60_000 } as const;

async function buscarEmailOriginal(ticketId: string, sinal: AbortSignal): Promise<string | null> {
  try {
    return await requisitarTexto(`/triagem/${encodeURIComponent(ticketId)}/email/original`, { sinal });
  } catch (causa) {
    // Mesma disciplina de `buscarDetalhe`: 404 não é falha da tela, é
    // "este ticket não tem e-mail de origem para ler" — `hasOriginEmail`
    // já avisa antes, mas a checagem aqui é a que realmente decide.
    if (causa instanceof ErroDaApi && causa.status === 404) return null;
    throw causa;
  }
}

/**
 * O `.eml` CRU do e-mail de origem (`AC-17`, `T6`), para exibir ao lado dos
 * campos lidos (`AC-30`, "sem navegar"). A API devolve
 * `application/octet-stream`; o front mostra o texto bruto — não há parser
 * de MIME no front (nenhuma dependência nova aprovada no plano para isso).
 */
export function opcoesDoEmailOriginalDaTriagem(ticketId: string) {
  return queryOptions({
    queryKey: [...chavesDeTriagem.detalhe(ticketId), 'email-original'] as const,
    queryFn: ({ signal }) => buscarEmailOriginal(ticketId, signal),
    ...CACHE_DO_EML,
  });
}

async function buscarEmailDaThread(ticketId: string, emailId: string, sinal: AbortSignal): Promise<string | null> {
  try {
    return await requisitarTexto(
      `/triagem/${encodeURIComponent(ticketId)}/emails/${encodeURIComponent(emailId)}/original`,
      { sinal },
    );
  } catch (causa) {
    // 404 = a mensagem não é deste ticket (ou o ticket não é desta sessão): mesma resposta
    // para os dois, e para a tela é "não há conteúdo", não falha.
    if (causa instanceof ErroDaApi && causa.status === 404) return null;
    throw causa;
  }
}

/**
 * O `.eml` CRU de uma mensagem QUALQUER da thread do ticket (`US-035` `AC9`) — a mesma forma de
 * `opcoesDoEmailOriginalDaTriagem`, para a mensagem escolhida na lista do detalhe.
 */
export function opcoesDoEmailDaThread(ticketId: string, emailId: string) {
  return queryOptions({
    queryKey: [...chavesDeTriagem.detalhe(ticketId), 'email', emailId] as const,
    queryFn: ({ signal }) => buscarEmailDaThread(ticketId, emailId, signal),
    ...CACHE_DO_EML,
  });
}

/** A URL de SESSÃO do `.eml` de uma mensagem da thread (`AC9`), para `href` de download. */
export function urlDoEmailDaThread(ticketId: string, emailId: string): string {
  return montarUrlDaApi(`/triagem/${encodeURIComponent(ticketId)}/emails/${encodeURIComponent(emailId)}/original`);
}

export { urlDoAnexoDaThread } from '../../../shared/triagem/urls';

/**
 * A URL de SESSÃO do `.eml` original (`AC-17`, `T6`) — distinta da rota de
 * máquina (`/emails/{id}/original`, `ADR-0002`). Usada em `href` (download
 * direto), não em `fetch`: é a navegação normal do browser que carrega o
 * cookie.
 */
export function urlDoEmailOriginal(ticketId: string): string {
  return montarUrlDaApi(`/triagem/${encodeURIComponent(ticketId)}/email/original`);
}

/** A URL de SESSÃO de um anexo do e-mail de origem (`AC-18`, `T6`). */
export function urlDoAnexo(ticketId: string, anexoId: string): string {
  return montarUrlDaApi(`/triagem/${encodeURIComponent(ticketId)}/email/anexos/${encodeURIComponent(anexoId)}`);
}
